import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, Grid, FormLabel} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import CurrencyYuanOutlinedIcon from '@mui/icons-material/CurrencyYuanOutlined';
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, iso2Date} from "@/helper/method";
import {CustomerPO} from "@/model/customer";
import {SearchContractVO} from "@/service/contract.s";
import {RoleUserVO} from "@/service/user.s";
import {useRoleUsers} from "@/store/user.c";
import {useCreateCopyright, useQueryCopyright, useUpdateCopyright} from "@/service/copyright.s";
import {isNumber} from "lodash";

const validationSchema = z.object({
        id: z.number().default(0),
        customerId: z.number().default(0),
        customerName: z.string({message: "请选择关联客户"}).nonempty({message: "请选择关联客户"}),
        writer: z.unknown(),
        writerId: z.number({message: "请填写撰稿人"}),
        writeStatus: z.string({message: "请选择撰写状态"}).nonempty({message: "请选择撰写状态"}),
        writeStatusId: z.unknown(),
        category: z.string({message: "请选择版权类型"}).nonempty({message: "请选择版权类型"}),
        categoryId: z.unknown(),
        kind: z.string({message: "请选择服务类型"}).nonempty({message: "请选择服务类型"}),
        kindId: z.unknown(),
        copyrightName: z.string({message: "请填写版权名称"}).nonempty({message: "请填写版权名称"}),
        registerNo: z.unknown(),
        // registerNo: z.string({message: "请填写登记号"}).nonempty({message: "请填写登记号"}),
        releaseDate: z.unknown().default(null),
        registerDate: z.unknown().default(null),
        finishDate: z.unknown().default(null),
        explain: z.unknown(),
        contractId: z.unknown().default(0),
        contractName: z.unknown(),
        insideDeadline: z.unknown().default(null),
        price: z.unknown(),
        sellerId:
            z.number({message: "请选择业务人员"}),
        assigner:
            z.unknown(),
        assignerId:
            z.number({message: "请选择分配人"}),
        seller:
            z.unknown(),
        tempWriter:
            z.object({username: z.string().default(''), id: z.number()}).optional(),
        tempAssigner:
            z.object({username: z.string().default(''), id: z.number()}).optional(),
        tempSeller:
            z.object({username: z.string().default(''), id: z.number()}).optional(),
        copyrightStatus:
            z.unknown({message: "请选择版权状态"}),
        copyrightStatusId:
            z.unknown(),
    })
;

export type WriteCopyrightForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteCopyright = () => {
    const allDict = useAllDict()
    const seller = useRoleUsers(["marketing", "manager"])
    const writer = useRoleUsers(["manager", "processer"])
    const assigner = useRoleUsers(["processer"])
    const {copyrightType, copyrightStatus, writeStatus, copyrightKind} = allDict
    const {setToast} = useGlobalActions()
    const {write, id: drawerId} = useDrawerModal()
    const {mutateAsync: createCopyrightMutate, isPending: createCopyrightPending} = useCreateCopyright()
    const {mutateAsync: updateCopyrightMutate, isPending: updateCopyrightPending} = useUpdateCopyright()
    const {data: copyrightData} = useQueryCopyright({id: drawerId!}, isNumber(drawerId))
    const copyright = copyrightData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {
                ...copyright, finishDate: iso2Date(copyright?.finishDate),
                writeStatusTime: iso2Date(copyright?.writeStatusTime), registerDate: iso2Date(copyright?.registerDate),
                releaseDate: iso2Date(copyright?.releaseDate), tempWriter: {username: copyright?.writer, id: copyright?.writerId},
                tempSeller: {username: copyright?.seller, id: copyright?.sellerId}, tempAssigner: {
                    username: copyright?.assigner,
                    id: copyright?.assignerId
                }, contractName: copyright?.contract?.contractName,
                insideDeadline: iso2Date(copyright?.insideDeadline)
            };
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteCopyrightForm>({
        values: initValues() as WriteCopyrightForm,
        resolver: zodResolver(validationSchema),
        defaultValues,
    });

    const onSubmit = async (values: WriteCopyrightForm) => {
        const result = write == 'add' ? await createCopyrightMutate(values) : await updateCopyrightMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>服务类型</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={copyrightKind}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("kindId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.kind}
                                           helperText={errors?.kind?.message}
                                           variant="outlined"
                                           placeholder={"选择服务类型"} {...params} />
                            )}
                        />} name={"kind"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>版权名称</FormLabel>
                        <TextField
                            error={!!errors?.copyrightName}
                            helperText={errors?.copyrightName?.message}
                            {...register("copyrightName")}
                            placeholder="填写版权名称"
                            variant="outlined"
                        /> </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>版权类型</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={copyrightType}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("categoryId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.category}
                                           helperText={errors?.category?.message}
                                           variant="outlined"
                                           placeholder={"选择版权类型"} {...params} />
                            )}
                        />} name={"category"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>版权状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={copyrightStatus}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("copyrightStatusId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.copyrightStatus}
                                           helperText={errors?.copyrightStatus?.message}
                                           variant="outlined"
                                           placeholder={"选择版权状态"} {...params} />
                            )}
                        />} name={"copyrightStatus"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true}>
                        <FormLabel>创作完成日期</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择创作完成日期"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'finishDate'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true}>
                        <FormLabel>登记日期</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择登记日期"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'registerDate'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>登记号</FormLabel>
                        <TextField
                            placeholder="填写登记号"
                            variant="outlined"
                            error={!!errors?.registerNo}
                            helperText={errors?.registerNo?.message}
                            {...register("registerNo")}
                        />
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>发布日期</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择发布日期"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'releaseDate'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>分配人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={assigner}
                                value={value}
                                onChange={(_, option: RoleUserVO | null) => {
                                    setValue("assignerId", option!.id)
                                    setValue("assigner", option?.username)
                                    onChange(option)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.assignerId}
                                               helperText={errors?.assignerId?.message}
                                               variant="outlined"
                                               placeholder={"选择分配人"} {...params} />
                                )}
                            />
                        }} name={"tempAssigner"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteCopyrightForm, CustomerPO> control={control} attr={"customerName"} attrId={"customerId"} errMsg={errors.customerName?.message}
                                                                setValue={setValue}
                                                                label={"客户名称"}/>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteCopyrightForm, SearchContractVO> control={control} tag={2} attr={"contractName"} autoLoad={true} attrId={"contractId"} setValue={setValue}
                                                                      label={"关联合同"}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>价格</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            value={value}
                            onChange={event => onChange(event.target.value)}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000000,
                                        maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <CurrencyYuanOutlinedIcon color="disabled"/>,
                                },
                            }}
                            placeholder="填写价格"
                            variant="outlined"
                        />} name={'price'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>业务人员</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={seller}
                                value={value}
                                onChange={(_, option: RoleUserVO | null) => {
                                    setValue("sellerId", option!.id)
                                    setValue("seller", option?.username)
                                    onChange(option)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.sellerId}
                                               helperText={errors?.sellerId?.message}
                                               variant="outlined"
                                               placeholder={"选择业务人员"} {...params} />
                                )}
                            />
                        }} name={"tempSeller"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>撰稿人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={writer}
                                value={value}
                                onChange={(_, option: RoleUserVO | null) => {
                                    setValue("writerId", option!.id)
                                    setValue("writer", option?.username)
                                    onChange(option)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.writerId}
                                               helperText={errors?.writerId?.message}
                                               variant="outlined"
                                               placeholder={"选择撰稿人"} {...params} />
                                )}
                            />
                        }} name={"tempWriter"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>撰写状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={writeStatus}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("writeStatusId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.writeStatus}
                                           helperText={errors?.writeStatus?.message}
                                           variant="outlined"
                                           placeholder={"选择撰写状态"} {...params} />
                            )}
                        />} name={"writeStatus"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>内部期限</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择内部期限"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'insideDeadline'}/>
                    </FormControl>
                </Grid>

                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>备注</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("explain")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                    inputProps: {
                                        maxLength: 250,
                                    },
                                },
                            }}
                            placeholder="输入备注"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createCopyrightPending || updateCopyrightPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
